// ===========================================
// "+ Call" and "+ Note" (enquiries plan §7, Phase 2)
// ===========================================
// Records a call, a text, an email sent from elsewhere, or a note in the enquiry's history. It
// moves no stage and passes no ball: an emailed reply does that (the reply box). What is saved
// can never be edited or deleted afterwards, by anyone (L7), so the dialog says so.
// The time defaults to now and may be earlier, never later (the server refuses a future time).
// "Set the next step" also saves the owner, the next action and its due day.

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { Enquiry, LoggableActivityType } from '../../../api/enquiry.api';
import { apiErrorMessage, useAssignableOwners, useEnquiryConfig, useLogEnquiryActivity } from '../../../hooks/useEnquiries';
import { instantToSchoolWallClock, schoolWallClockToInstant } from '../../../utils/dateUtils';
import { dayKey, stageView } from './enquiryUi';

export type LogKind = 'CALL' | 'NOTE';

export interface LogActivityDialogProps {
  enquiry: Enquiry;
  kind: LogKind;
  open: boolean;
  onClose: () => void;
}

const GROUP = '';
const MAX_ACTION = 200;
const NOTES_MAX = 10_000;

/** `YYYY-MM-DDTHH:mm` in the browser's clock: only until the config (the school's zone) loads. */
function localInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The "When" field is the SCHOOL's wall clock (review L9: a member of staff outside the school's
 * zone would otherwise log the call hours off). `config.timeZone` is the school's zone.
 */
function toInputValue(d: Date, timeZone: string | undefined): string {
  return timeZone ? instantToSchoolWallClock(d, timeZone) : localInputValue(d);
}
function fromInputValue(value: string, timeZone: string | undefined): Date | null {
  if (timeZone) return schoolWallClockToInstant(value, timeZone);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export default function LogActivityDialog({ enquiry, kind, open, onClose }: LogActivityDialogProps) {
  const { data: config } = useEnquiryConfig();
  const owned = stageView(config, enquiry.stage)?.isOwned === true;
  const log = useLogEnquiryActivity({ quietErrors: true });

  const [type, setType] = useState<LoggableActivityType>(kind === 'NOTE' ? 'INTERNAL_NOTE' : 'PHONE_CALL');
  const [direction, setDirection] = useState<'OUTBOUND' | 'INBOUND'>('OUTBOUND');
  const [notes, setNotes] = useState('');
  const [when, setWhen] = useState('');
  const [setsNext, setSetsNext] = useState(false);
  const [ownerId, setOwnerId] = useState<string>(GROUP);
  const [action, setAction] = useState('');
  const [due, setDue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: owners, isError: ownersError } = useAssignableOwners(open && setsNext);

  useEffect(() => {
    if (!open) return;
    setType(kind === 'NOTE' ? 'INTERNAL_NOTE' : 'PHONE_CALL');
    setDirection('OUTBOUND');
    setNotes('');
    setWhen('');
    setSetsNext(false);
    setOwnerId(enquiry.assignedToId ?? GROUP);
    setAction(enquiry.nextAction ?? '');
    setDue(dayKey(enquiry.nextActionDueDate) ?? '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind, enquiry.id]);

  const isNote = type === 'INTERNAL_NOTE';
  const timeZone = config?.timeZone;
  const nowValue = toInputValue(new Date(), timeZone);
  const whenInstant = when ? fromInputValue(when, timeZone) : null;
  const whenFuture = !!whenInstant && whenInstant.getTime() > Date.now() + 60_000;
  const minDue = config?.schoolToday;
  const duePast = !!due && !!minDue && due < minDue;
  const nextValid = !setsNext || (action.trim().length > 0 && !!due && !duePast);
  const valid = (!isNote || notes.trim().length > 0) && !whenFuture && nextValid;

  const ownerOptions = (Array.isArray(owners) ? owners : []).map((o) => ({ id: o.id, name: `${o.firstName} ${o.lastName}` }));
  if (enquiry.assignedToId && !ownerOptions.some((o) => o.id === enquiry.assignedToId)) {
    const who = enquiry.AssignedTo ? `${enquiry.AssignedTo.firstName} ${enquiry.AssignedTo.lastName}` : 'Current owner';
    ownerOptions.push({ id: enquiry.assignedToId, name: `${who} (current owner)` });
  }

  const handleSave = () => {
    if (!valid) return;
    setError(null);
    log.mutate(
      {
        id: enquiry.id,
        data: {
          type,
          ...(isNote ? {} : { direction }),
          notes: notes.trim() ? notes : null,
          ...(whenInstant ? { occurredAt: whenInstant.toISOString() } : {}),
          ...(setsNext
            ? { nextAction: action.trim(), nextActionDueDate: due, ownerUserId: ownerId === GROUP ? null : ownerId }
            : {}),
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (e) => setError(apiErrorMessage(e, 'Failed to save to the history')),
      },
    );
  };

  return (
    <Dialog open={open} onClose={log.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{kind === 'NOTE' ? 'Add a note' : 'Log a call'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {kind === 'CALL' && (
            <>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={type}
                onChange={(_, v: LoggableActivityType | null) => v && setType(v)}
                aria-label="What kind of contact"
              >
                <ToggleButton value="PHONE_CALL">Call</ToggleButton>
                <ToggleButton value="SMS">Text</ToggleButton>
                <ToggleButton value="EMAIL">Email</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={direction}
                onChange={(_, v: 'OUTBOUND' | 'INBOUND' | null) => v && setDirection(v)}
                aria-label="Who made contact"
              >
                <ToggleButton value="OUTBOUND">We contacted them</ToggleButton>
                <ToggleButton value="INBOUND">They contacted us</ToggleButton>
              </ToggleButtonGroup>
            </>
          )}
          <TextField
            label={isNote ? 'Note' : 'What was said'}
            required={isNote}
            multiline
            minRows={3}
            fullWidth
            size="small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ maxLength: NOTES_MAX }}
          />
          <TextField
            label="When"
            type="datetime-local"
            size="small"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: nowValue }}
            error={whenFuture}
            helperText={whenFuture ? 'That time is in the future.' : 'Leave empty for now'}
          />
          {owned && (
            <FormControlLabel
              control={<Checkbox checked={setsNext} onChange={(e) => setSetsNext(e.target.checked)} />}
              label="Set the next step"
            />
          )}
          {owned && setsNext && (
            <>
              {ownersError && <Alert severity="warning">Could not load the list of people.</Alert>}
              <FormControl fullWidth size="small">
                <InputLabel id="log-owner-label">Owner</InputLabel>
                <Select
                  labelId="log-owner-label"
                  label="Owner"
                  value={ownerOptions.some((o) => o.id === ownerId) ? ownerId : GROUP}
                  onChange={(e) => setOwnerId(String(e.target.value))}
                >
                  <MenuItem value={GROUP}>Back to the group</MenuItem>
                  {ownerOptions.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Next action"
                required
                fullWidth
                size="small"
                value={action}
                onChange={(e) => setAction(e.target.value)}
                inputProps={{ maxLength: MAX_ACTION }}
              />
              <TextField
                label="Due date"
                type="date"
                required
                fullWidth
                size="small"
                value={due}
                onChange={(e) => setDue(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minDue }}
                error={duePast}
                helperText={duePast ? 'The due date has passed.' : undefined}
              />
            </>
          )}
          <Typography variant="caption" color="text.secondary">
            Once saved, this cannot be changed or deleted by anyone.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={log.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!valid || log.isPending}>
          {log.isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
